import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useId } from "react";
import { MoveBpaParsedSummary } from "./MoveBpaParsedSummary.js";
import { MoveBpaRunReport } from "./MoveBpaRunReport.js";
import { useMoveBpaRun, type MoveBlock } from "./MoveBpaRunProvider.js";
import { OPTIONAL_COLUMNS, REQUIRED_COLUMNS } from "./parseMoveBpaCsv.js";
import styles from "../PipelineBrowser.module.css";

function plural(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

function moveHint(block: MoveBlock): string {
  if (block.kind === "invalid_rows") {
    return `${plural(block.count, "invalid row")}. Fix the file before running the move.`;
  }
  if (block.kind === "failed_rows") {
    return `${plural(block.count, "row")} failed in the dry run. Fix the file and run the dry run again.`;
  }
  return "Run move needs a finished dry run of this file first.";
}

export function MoveBpaCsvSection() {
  const hintId = useId();
  const {
    file,
    parsing,
    parseError,
    selectFile,
    dryRunTotals,
    moveBlock,
    startDryRun,
    startMove,
    running,
    stopping,
    stopped,
    dryRun,
    progress,
    results,
    totals,
    stop,
    registerSection,
  } = useMoveBpaRun();

  useEffect(() => registerSection(), [registerSection]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0];
      event.target.value = "";
      if (selected) void selectFile(selected);
    },
    [selectFile],
  );

  const rows = file?.parsed.valid ?? [];
  const canRun = !running && !parsing && file !== null && rows.length > 0;

  const handleMove = useCallback(() => {
    if (!dryRunTotals) return;
    const confirmed = window.confirm(
      `Move testimonials for ${plural(rows.length, "row")}?\n\nThe dry run matched ${dryRunTotals.roots} root items and ${dryRunTotals.children} child items (${dryRunTotals.alreadyInPlace} already in place). Dry run rows: ${dryRunTotals.ok} ok, ${dryRunTotals.partial} partial, ${dryRunTotals.failed} failed.\n\nThis re-files them in approval-api, testimonials and processing items. There is no undo button.\n\nContinue?`,
    );
    if (!confirmed) return;
    void startMove();
  }, [startMove, rows.length, dryRunTotals]);

  const hasResult = !running && progress.totalRows > 0;
  const showMoveHint = canRun && moveBlock !== null;

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Move BPAs from CSV</div>
      <div className={styles.infoBanner}>
        Moves every testimonial of a firm within one practice-area group (parent + sub-tables) to
        the table where the firm is published. Translations (child items) move with their parent.
        Run a dry run first. Required columns: <code>{REQUIRED_COLUMNS.join(",")}</code>. Optional:{" "}
        <code>{OPTIONAL_COLUMNS.join(",")}</code>. One row per firm per group;{" "}
        <code>source_practice_area_ids</code> is <code>;</code>-separated and must contain the
        target.
      </div>

      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>CSV file:</span>
        <input
          type="file"
          accept=".csv"
          aria-label="Move BPAs CSV file"
          disabled={running || parsing}
          onChange={handleFileChange}
        />
      </div>

      {parsing && <div className={styles.syncStateMessage}>Parsing CSV…</div>}
      {parseError && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {parseError}
        </div>
      )}
      {file && <MoveBpaParsedSummary fileName={file.name} parsed={file.parsed} />}

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!canRun}
          onClick={() => void startDryRun()}
        >
          Dry run
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={!canRun || moveBlock !== null}
          aria-describedby={showMoveHint ? hintId : undefined}
          onClick={handleMove}
        >
          Run move
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={!running || stopping}
          onClick={stop}
        >
          {stopping ? "Stopping after current row…" : "Stop"}
        </Button>
        {showMoveHint && moveBlock && (
          <span id={hintId} className={styles.mappingHint}>
            {moveHint(moveBlock)}
          </span>
        )}
      </div>

      {running && (
        <>
          <div className={styles.warningBox}>
            Do not close or refresh this tab. The move runs from this browser tab one CSV row at a
            time, and closing or refreshing stops it part way. Other staff-ui pages are fine: the
            run carries on, a banner at the top shows its progress, and the report is here when you
            come back. Re-running the same file is safe, items already in place are skipped.
          </div>
          <div className={styles.syncStateMessage} role="status">
            {dryRun ? "Dry run" : "Moving"}: row {progress.processedRows} / {progress.totalRows}
          </div>
          <div className={styles.syncStateMessage}>
            Roots {totals.roots} · children {totals.children} · already in place{" "}
            {totals.alreadyInPlace} · rows failed {totals.failed} · partial {totals.partial} ·
            unknown {totals.unknown}
          </div>
        </>
      )}

      {hasResult && (
        <MoveBpaRunReport
          totals={totals}
          results={results}
          progress={progress}
          dryRun={dryRun}
          stopped={stopped}
        />
      )}
    </div>
  );
}
